import { getTonUsdRate } from "./tonUsdRate";

const META_PIXEL_SCRIPT_URL = "https://connect.facebook.net/en_US/fbevents.js";
const REGISTRATION_STORAGE_KEY = "metaPixelRegistrations";
const SENSITIVE_QUERY_PARAMS = ["ref", "startapp", "start", "tgWebAppStartParam"];

type FbqArgs = unknown[];

interface Fbq {
  (...args: FbqArgs): void;
  callMethod?: (...args: FbqArgs) => void;
  queue: FbqArgs[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
  allowDuplicatePageViews?: boolean;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export interface MetaPixelPurchaseDetails {
  contentName?: string;
  contentIds?: string[];
  numItems?: number;
  /** Used only locally to fire once per transaction. Never sent to Meta as is. */
  transactionId?: string;
}

export interface MetaPixelPurchase extends MetaPixelPurchaseDetails {
  /** Sent together with currency; when either is missing, Purchase goes without value and currency. */
  value?: number;
  currency?: string;
}

export interface MetaPixelTonPurchase extends MetaPixelPurchaseDetails {
  /** Amount paid for the tickets, in TON. */
  amountTon: number;
}

export interface MetaPixelDeposit {
  value: number;
  currency: string;
  /** Used only locally to fire once per transaction. Never sent to Meta as is. */
  transactionId?: string;
}

let initialized = false;
let invalidIdLogged = false;
let lastPageKey: string | null = null;
let anonymousRegistrationTracked = false;
const firedEventKeys = new Set<string>();
const trackedRegistrations = new Set<string>();

function getPixelId(): string | null {
  const pixelId = (process.env.REACT_APP_META_PIXEL_ID || "").trim();
  if (!pixelId) return null;

  if (!/^\d{5,20}$/.test(pixelId)) {
    if (!invalidIdLogged) {
      console.warn("[MetaPixel] REACT_APP_META_PIXEL_ID has invalid format, pixel is disabled");
      invalidIdLogged = true;
    }
    return null;
  }

  return pixelId;
}

// Non-reversible short key, so raw wallet addresses / transaction BOCs are never stored or sent.
function hashKey(value: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 0x01000193);
    h2 = Math.imul(h2 + code, 0x85ebca6b) ^ (h2 >>> 13);
  }
  return (h1 >>> 0).toString(16).padStart(8, "0") + (h2 >>> 0).toString(16).padStart(8, "0");
}

// Pixel reports the full page URL, so everything sensitive is removed from it before the pixel starts:
// - Telegram Mini App launch data in the hash (#tgWebAppData=... carries Telegram user data);
// - entry params that may hold a referrer wallet address or ad click ids.
// The app must persist these params (referral / Adsgram) before initMetaPixel is called.
function stripSensitiveParamsFromUrl() {
  try {
    const { pathname, search, hash } = window.location;

    const searchParams = new URLSearchParams(search);
    let searchChanged = false;
    SENSITIVE_QUERY_PARAMS.forEach((param) => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        searchChanged = true;
      }
    });

    const stripHash = hash.includes("tgWebAppData");
    if (!searchChanged && !stripHash) return;

    const nextSearch = searchParams.toString();
    window.history.replaceState(
      window.history.state,
      "",
      pathname + (nextSearch ? `?${nextSearch}` : "") + (stripHash ? "" : hash)
    );
  } catch {
    // Ignore history errors.
  }
}

function readStoredRegistrations(): string[] {
  try {
    const raw = window.localStorage.getItem(REGISTRATION_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function isRegistrationTracked(key: string): boolean {
  return trackedRegistrations.has(key) || readStoredRegistrations().includes(key);
}

function rememberRegistration(key: string) {
  trackedRegistrations.add(key);
  try {
    const stored = readStoredRegistrations();
    if (!stored.includes(key)) {
      window.localStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify([...stored, key]));
    }
  } catch {
    // Ignore storage errors.
  }
}

function send(
  method: "track" | "trackCustom",
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string
): boolean {
  if (!initialized || typeof window === "undefined" || !window.fbq) return false;

  try {
    if (eventId) {
      window.fbq(method, eventName, params ?? {}, { eventID: eventId });
    } else if (params) {
      window.fbq(method, eventName, params);
    } else {
      window.fbq(method, eventName);
    }
    return true;
  } catch (error) {
    console.warn("[MetaPixel] failed to send event:", eventName, error);
    return false;
  }
}

function isValidAmount(value: number, currency: string): boolean {
  return Number.isFinite(value) && value >= 0 && currency.trim().length > 0;
}

export function isMetaPixelEnabled(): boolean {
  return initialized;
}

export function initMetaPixel(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (initialized) return true;

  const pixelId = getPixelId();
  if (!pixelId) return false;

  stripSensitiveParamsFromUrl();

  if (!window.fbq) {
    const fbq = function (...args: FbqArgs) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    } as Fbq;

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = META_PIXEL_SCRIPT_URL;
    document.head.appendChild(script);
  }

  // SPA page views are sent manually via trackPageView on every route change, so the pixel's own
  // History API listener is turned off. The pixel then drops every PageView after the first one
  // as a duplicate, unless allowDuplicatePageViews is set.
  window.fbq.disablePushState = true;
  window.fbq.allowDuplicatePageViews = true;
  window.fbq("init", pixelId);

  initialized = true;
  return true;
}

/**
 * The pixel reads the page URL from window.location at the moment of the call,
 * so this must be called after the route change is applied to the address bar.
 *
 * @param pageKey key of the current page (path + query); the same key is never tracked twice in a row.
 */
export function trackPageView(pageKey?: string): boolean {
  if (!initialized) return false;
  if (pageKey !== undefined && pageKey === lastPageKey) return false;

  const sent = send("track", "PageView");
  if (sent && pageKey !== undefined) lastPageKey = pageKey;
  return sent;
}

/**
 * Marks a user as already known, so CompleteRegistration is not sent for them later
 * (e.g. a wallet session that existed before the pixel was installed).
 */
export function markRegistrationTracked(userKey: string) {
  if (!initialized || typeof window === "undefined" || !userKey) return;
  rememberRegistration(hashKey(userKey));
}

/**
 * @param userKey stable user identifier, used only locally (hashed) to fire once per user per browser.
 */
export function trackRegistration(userKey?: string): boolean {
  if (!initialized || typeof window === "undefined") return false;

  if (!userKey) {
    if (anonymousRegistrationTracked) return false;
    const sent = send("track", "CompleteRegistration");
    if (sent) anonymousRegistrationTracked = true;
    return sent;
  }

  const key = hashKey(userKey);
  if (isRegistrationTracked(key)) return false;

  const sent = send("track", "CompleteRegistration");
  if (sent) rememberRegistration(key);
  return sent;
}

function roundUsd(value: number): number {
  // Cents are enough, except for sub-cent purchases that would otherwise become 0.
  const decimals = value >= 0.01 ? 2 : 4;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function trackPurchase(purchase: MetaPixelPurchase): boolean {
  if (!initialized) return false;

  const eventId = purchase.transactionId ? `purchase-${hashKey(purchase.transactionId)}` : undefined;
  if (eventId && firedEventKeys.has(eventId)) return false;

  const params: Record<string, unknown> = {};
  if (
    purchase.value !== undefined &&
    purchase.currency !== undefined &&
    isValidAmount(purchase.value, purchase.currency)
  ) {
    params.value = purchase.value;
    params.currency = purchase.currency;
  }
  if (purchase.contentName) params.content_name = purchase.contentName;
  if (purchase.contentIds && purchase.contentIds.length > 0) {
    params.content_ids = purchase.contentIds;
    params.content_type = "product";
  }
  if (purchase.numItems !== undefined) params.num_items = purchase.numItems;

  const sent = send("track", "Purchase", params, eventId);
  if (sent && eventId) firedEventKeys.add(eventId);
  return sent;
}

/**
 * Warms up the TON/USD rate cache when a purchase starts, so the Purchase event
 * can be sent right after the purchase succeeds.
 */
export function prepareTonPurchaseTracking() {
  if (!initialized) return;
  getTonUsdRate().catch(() => {});
}

/**
 * Sends Purchase for a ticket paid in TON. Meta gets the USD equivalent by the current TonAPI rate;
 * when the rate is unavailable, Purchase is still sent, but without value and currency.
 */
export async function trackTonPurchase(purchase: MetaPixelTonPurchase): Promise<boolean> {
  if (!initialized) return false;

  const { amountTon, ...details } = purchase;

  let rate: number | null = null;
  try {
    rate = await getTonUsdRate();
  } catch {
    rate = null;
  }

  if (rate === null || !Number.isFinite(amountTon) || amountTon < 0) {
    return trackPurchase(details);
  }

  return trackPurchase({ ...details, value: roundUsd(amountTon * rate), currency: "USD" });
}

export function trackDeposit(deposit: MetaPixelDeposit): boolean {
  if (!initialized) return false;
  if (!isValidAmount(deposit.value, deposit.currency)) return false;

  const eventId = deposit.transactionId ? `deposit-${hashKey(deposit.transactionId)}` : undefined;
  if (eventId && firedEventKeys.has(eventId)) return false;

  const sent = send(
    "trackCustom",
    "Deposit",
    { value: deposit.value, currency: deposit.currency },
    eventId
  );
  if (sent && eventId) firedEventKeys.add(eventId);
  return sent;
}

/** Test helper: resets module state between test cases. */
export function resetMetaPixelForTests() {
  initialized = false;
  invalidIdLogged = false;
  lastPageKey = null;
  anonymousRegistrationTracked = false;
  firedEventKeys.clear();
  trackedRegistrations.clear();
}
